import React from "react";
import StaffAvatarCircle from "../../components/StaffAvatarCircle";
import { employeeRowForAvatar } from "../../utils/accountAvatarUrl";
import { shiftNetWorkHours } from "../../utils/scheduleDurationUtils";
import { formatTimeRange } from "./clientScheduleGridUtils";
import ClientScheduleCheckInBadge from "./ClientScheduleCheckInBadge";

function noteText(value) {
  return value != null && String(value).trim().length > 0
    ? String(value).trim()
    : "";
}

function NotePencilIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-8.086 8.086A2 2 0 0 1 7.293 15H4a1 1 0 0 1-1-1v-3.293a2 2 0 0 1 .586-1.414l8-8Z" />
      <path d="M5 13h2.586L14 6.586 12.414 5 5 12.414V13Z" />
    </svg>
  );
}

function assignmentEmployee(assignment) {
  return employeeRowForAvatar({
    account_id: assignment.account_id,
    name: assignment.name,
    first_name: assignment.first_name,
    last_name: assignment.last_name,
    image_type: assignment.image_type,
    image_time: assignment.image_time,
  });
}

function assignmentRowClass(state) {
  const s = String(state ?? "").trim().toLowerCase();
  if (s === "confirmed" || s === "completed" || s === "in_progress") {
    return "bg-green-100 border border-green-500";
  }
  if (s === "assigned" || s === "active") {
    return "bg-green-50 border border-green-300";
  }
  return "bg-white border border-gray-200";
}

export default function ClientScheduleShiftRow({ shift, onShowNote }) {
  const timeRange = formatTimeRange(shift.start_date, shift.end_date);
  const hoursLabel = `${shiftNetWorkHours(shift)}h`;
  const assignments = shift.assignments || [];
  const tempNote = noteText(shift.note_to_staff ?? shift.note);
  const adminNote = noteText(shift.admin_note);
  const hasTempIcon = !!tempNote;
  const hasAdminIcon = !!adminNote;

  const openNote = (type, e) => {
    e.stopPropagation();
    onShowNote?.({
      type,
      tempText: tempNote,
      adminText: adminNote,
    });
  };

  return (
    <div
      data-shift-id={shift.shift_id}
      className="bg-white rounded border border-gray-200 p-2"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-600">{timeRange}</div>
        <div className="text-xs font-semibold px-1.5 py-0.5 rounded text-gray-800 bg-gray-100">
          {hoursLabel}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-medium text-gray-800 truncate">
          {shift.position_name}
        </div>
        {(hasTempIcon || hasAdminIcon) && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {hasTempIcon ? (
              <button
                type="button"
                className="p-0.5 rounded-sm border border-red-800 text-red-600 hover:bg-red-50"
                aria-label="Temp staff note"
                onClick={(e) => openNote("temp", e)}
              >
                <NotePencilIcon className="w-3 h-3" />
              </button>
            ) : null}
            {hasAdminIcon ? (
              <button
                type="button"
                className="p-0.5 rounded-sm border border-gray-900 text-gray-900 hover:bg-gray-100"
                aria-label="Admin note"
                onClick={(e) => openNote("admin", e)}
              >
                <NotePencilIcon className="w-3 h-3" />
              </button>
            ) : null}
          </div>
        )}
      </div>

      {assignments.length === 0 ? (
        <div className="flex items-center justify-between p-2 rounded bg-white border border-gray-200">
          <div className="text-xs text-gray-500 italic">Unassigned</div>
          <ClientScheduleCheckInBadge shift={shift} />
        </div>
      ) : (
        <div className="space-y-1.5">
          {assignments.map((assignment, index) => {
            const employee = assignmentEmployee(assignment);
            const showStatus = index === 0;
            return (
              <div
                key={`${shift.shift_id}-${assignment.account_id}`}
                className={`flex items-center justify-between gap-2 p-2 rounded ${assignmentRowClass(assignment.state)}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <StaffAvatarCircle
                    accountId={assignment.account_id}
                    employee={employee}
                    name={assignment.name}
                    className="h-6 w-6 flex-shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-gray-900 truncate">
                      {assignment.name || "Staff"}
                    </span>
                    <span className="text-[10px] text-gray-500 truncate">
                      {timeRange}
                    </span>
                  </div>
                </div>
                {showStatus ? <ClientScheduleCheckInBadge shift={shift} /> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
